import React, { useEffect, useMemo, useState } from 'react';
import { Table, Button, Form, Pagination, Modal, Alert, OverlayTrigger, Tooltip, Badge, Card, Spinner } from 'react-bootstrap';
import Sidebar from '../components/Sidebar';
import { apiGet, apiPost, API_BASE_URL } from '../utils/apiConfig';
import 'bootstrap-icons/font/bootstrap-icons.css';

const documentosPorPagina = 10;

// ⭐ V5.8: Fecha de corte — antes: local, después: Certicámara
// 09/04/2026 17:00 hora Colombia (UTC-5) = 22:00 UTC
const FECHA_CORTE = new Date('2026-04-09T22:00:00.000Z');

/**
 * Determina si un documento debe consultarse desde Certicámara
 * o desde el almacenamiento local según su fecha de creación.
 */
const usaCerticamara = (doc) => {
  if (!doc?.createdAt) return false;
  return new Date(doc.createdAt) >= FECHA_CORTE;
};

function MedicalDocumentsPage() {
  const [documentos, setDocumentos] = useState([]);
  const [total, setTotal] = useState(0);
  const [pagina, setPagina] = useState(1);
  const [sidebarAbierto, setSidebarAbierto] = useState(window.innerWidth >= 992);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 992);
  const [mostrarModal, setMostrarModal] = useState(false);
  const [documentoSeleccionado, setDocumentoSeleccionado] = useState(null);
  const [sinPermisos, setSinPermisos] = useState(false);
  const nombres = localStorage.getItem('nombres') || 'Usuario';

  // Estados para filtros
  const [filtros, setFiltros] = useState({
    cedula: '', correo: '', funcionario: '', estado: '', fechaDesde: '', fechaHasta: ''
  });
  const [mostrarFiltrosAvanzados, setMostrarFiltrosAvanzados] = useState(false);

  // Estados para reenvío
  const [mostrarModalReenvio, setMostrarModalReenvio] = useState(false);
  const [documentoReenvio, setDocumentoReenvio] = useState(null);
  const [correoReenvio, setCorreoReenvio] = useState('');
  const [celularReenvio, setCelularReenvio] = useState('');
  const [metodosReenvio, setMetodosReenvio] = useState([]);
  const [enviandoReenvio, setEnviandoReenvio] = useState(false);
  const [mensajeReenvio, setMensajeReenvio] = useState('');

  // ⭐ V5.8: Estado de descarga para botones de Certicámara
  const [descargandoCerticamara, setDescargandoCerticamara] = useState({});

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 992);
    window.addEventListener('resize', handleResize);
    handleResize();
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const decodificarNombre = (nombre) => {
    if (!nombre) return '';
    try { return decodeURIComponent(nombre); } catch { return nombre; }
  };

  const totalPaginas = useMemo(() => Math.ceil(total / documentosPorPagina) || 1, [total]);

  const estadoBadge = (estado) => {
    const map = { rechazado: 'danger', pendiente: 'warning', finalizado: 'success', firmado: 'info', anulado: 'secondary', en_firma: 'primary' };
    const iconos = { rechazado: '❌ ', pendiente: '⌛ ', finalizado: '✅ ', firmado: '✍️ ', anulado: '🚫 ', en_firma: '✍️ ' };
    return (
      <Badge bg={map[estado] || 'secondary'} className={estado === 'pendiente' ? 'text-dark' : ''}>
        {iconos[estado] || ''}{(estado || '').toUpperCase()}
      </Badge>
    );
  };

  const copyToClipboard = (text) => navigator.clipboard?.writeText(text).catch(() => {});

  const renderMetodoEnvio = (metodos) => {
    let metodosArray = [];
    if (Array.isArray(metodos)) {
      if (metodos.length > 0 && typeof metodos[0] === 'string' && metodos[0].startsWith('[')) {
        try { metodosArray = JSON.parse(metodos[0]); } catch { metodosArray = metodos; }
      } else { metodosArray = metodos; }
    } else if (typeof metodos === 'string') {
      try { metodosArray = JSON.parse(metodos); } catch { metodosArray = [metodos]; }
    }
    if (metodosArray.length === 0) return <span className="text-muted small">N/A</span>;
    return (
      <div className="d-flex gap-2 flex-wrap justify-content-center">
        {metodosArray.map((m, i) => {
          const map = { correo: ['bi-envelope-fill', 'Correo', 'primary'], sms: ['bi-chat-dots-fill', 'SMS', 'success'], whatsapp: ['bi-whatsapp', 'WhatsApp', 'success'] };
          const conf = map[m];
          return conf ? (
            <OverlayTrigger key={i} placement="top" overlay={<Tooltip>{conf[1]}</Tooltip>}>
              <i className={`bi ${conf[0]} text-${conf[2]}`} style={{ fontSize: '1.2rem' }} />
            </OverlayTrigger>
          ) : null;
        })}
      </div>
    );
  };

  // ═══════════════════════════════════════════════════════════════════
  // ⭐ V5.8: HANDLERS DE CERTICÁMARA (igual que AdministrativeDocumentsPage)
  // ═══════════════════════════════════════════════════════════════════

  const handleVerDocumentoCerticamara = async (doc) => {
  if (doc.estadoCerticamara === 'firmado') {
    setDescargandoCerticamara(prev => ({ ...prev, [doc._id]: 'ver' }));
    try {
      // ⭐ V5.8: El endpoint ahora devuelve el PDF directamente como stream
      const url = `${API_BASE_URL}/api/certicamara/documento-firmado/${doc._id}`;
      setDocumentoSeleccionado(url);
      setMostrarModal(true);
    } finally {
      setDescargandoCerticamara(prev => ({ ...prev, [doc._id]: null }));
    }
  } else if (doc.rutaFinal?.trim()) {
    setDocumentoSeleccionado(`${API_BASE_URL}/${doc.rutaFinal}`);
    setMostrarModal(true);
  }
};

  const handleDescargarCertificadoBiofirmas = async (doc) => {
    if (!doc.rutaCertificadoBiofirmas) {
      alert('No hay certificado BIOFIRMAS disponible para este documento.');
      return;
    }
    setDescargandoCerticamara(prev => ({ ...prev, [`biofirma-${doc._id}`]: 'biofirma' }));
    try {
      const url = `${API_BASE_URL}/${doc.rutaCertificadoBiofirmas}`;
      const pdfResponse = await fetch(url);
      if (pdfResponse.ok) {
        const pdfBlob = await pdfResponse.blob();
        const blobUrl = URL.createObjectURL(new Blob([pdfBlob], { type: 'application/pdf' }));
        setDocumentoSeleccionado(blobUrl);
        setMostrarModal(true);
      } else {
        alert('No se pudo descargar el certificado BIOFIRMAS.');
      }
    } catch {
      alert('Error al descargar el certificado BIOFIRMAS.');
    } finally {
      setDescargandoCerticamara(prev => ({ ...prev, [`biofirma-${doc._id}`]: null }));
    }
  };

  const handleDescargarCertificado = async (doc) => {
    setDescargandoCerticamara(prev => ({ ...prev, [`cert-${doc._id}`]: 'certificado' }));
    try {
      const response = await apiGet(`/api/certicamara/certificado/${doc._id}`);
      if (response.certificado) {
        const cert = response.certificado;
        if (cert.url) { window.open(cert.url, '_blank'); return; }
        const base64Content = cert.content || (typeof cert === 'string' ? cert : null);
        const fileName = cert.fileName || `certificado_${doc.nombreDocumento || doc._id}.pdf`;
        if (base64Content && typeof base64Content === 'string' && base64Content.length > 100) {
          try {
            const byteCharacters = atob(base64Content);
            const byteNumbers = new Array(byteCharacters.length);
            for (let i = 0; i < byteCharacters.length; i++) byteNumbers[i] = byteCharacters.charCodeAt(i);
            const byteArray = new Uint8Array(byteNumbers);
            const blob = new Blob([byteArray], { type: 'application/pdf' });
            const blobUrl = URL.createObjectURL(blob);
            setDocumentoSeleccionado(blobUrl);
            setMostrarModal(true);
          } catch {
            try {
              const link = document.createElement('a');
              link.href = `data:application/pdf;base64,${base64Content}`;
              link.download = fileName;
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);
            } catch { alert('Error al descargar el certificado.'); }
          }
        } else { alert('El certificado no tiene un formato reconocido.'); }
      } else { alert('No se recibió certificado de Certicámara.'); }
    } catch {
      alert('No se pudo obtener el certificado. Intente más tarde.');
    } finally {
      setDescargandoCerticamara(prev => ({ ...prev, [`cert-${doc._id}`]: null }));
    }
  };

  // ═══════════════════════════════════════════════════════════════════
  // ⭐ V5.8: COMPONENTE DE ACCIONES — lógica de fecha de corte
  // ═══════════════════════════════════════════════════════════════════
  const AccionesDocumento = ({ doc }) => {
    const esCerticamaraFirmado = doc.estadoCerticamara === 'firmado';
    const tieneRutaLocal = doc.rutaFinal?.trim();
    const estaDescargando = descargandoCerticamara[doc._id];
    const estaCertificando = descargandoCerticamara[`cert-${doc._id}`];
    const usaModoCerticamara = usaCerticamara(doc);

    // ── Pendiente ──
    if (doc.estado === 'pendiente' || doc.estado === 'en_firma') {
      return (
        <div className="d-flex justify-content-center gap-1">
          <Button variant="outline-warning" size="sm" title="Reenviar" onClick={() => handleAbrirReenvio(doc)}>
            <i className="bi bi-arrow-repeat"></i>
          </Button>
          {doc.estado === 'en_firma' && (
            <OverlayTrigger overlay={<Tooltip>Firmado en BIOFIRMAS, esperando Certicámara</Tooltip>}>
              <span><Spinner size="sm" variant="primary" /></span>
            </OverlayTrigger>
          )}
        </div>
      );
    }

    // ── Rechazado ──
    if (doc.estado === 'rechazado') {
      const rutaVer = tieneRutaLocal ? `${API_BASE_URL}/${doc.rutaFinal}` : null;
      return rutaVer ? (
        <Button variant="outline-danger" size="sm" onClick={() => { setDocumentoSeleccionado(rutaVer); setMostrarModal(true); }}>
          <i className="bi bi-eye-fill"></i>
        </Button>
      ) : <span className="text-muted small">N/A</span>;
    }

    // ── Anulado ──
    if (doc.estado === 'anulado') return <span className="text-muted">🚫</span>;

    // ── Finalizado ──
    if (doc.estado === 'finalizado' || doc.estado === 'firmado') {

      // ── DOCUMENTOS ANTERIORES AL CORTE → botones locales ──
      if (!usaModoCerticamara) {
        return tieneRutaLocal ? (
          <div className="d-flex justify-content-center gap-1">
            <OverlayTrigger overlay={<Tooltip>Ver documento</Tooltip>}>
              <Button variant="primary" size="sm" onClick={() => { setDocumentoSeleccionado(`${API_BASE_URL}/${doc.rutaFinal}`); setMostrarModal(true); }}>
                <i className="bi bi-eye-fill"></i>
              </Button>
            </OverlayTrigger>
            <OverlayTrigger overlay={<Tooltip>Descargar documento</Tooltip>}>
              <a href={`${API_BASE_URL}/${doc.rutaFinal}`} download target="_blank" rel="noopener noreferrer" className="btn btn-sm btn-success">
                <i className="bi bi-download"></i>
              </a>
            </OverlayTrigger>
            {doc.rutaCertificadoBiofirmas && (
              <OverlayTrigger overlay={<Tooltip>Certificado BIOFIRMAS</Tooltip>}>
                <Button variant="outline-success" size="sm" onClick={() => handleDescargarCertificadoBiofirmas(doc)}
                  disabled={!!descargandoCerticamara[`biofirma-${doc._id}`]}>
                  {descargandoCerticamara[`biofirma-${doc._id}`] ? <Spinner size="sm" /> : <i className="bi bi-download"></i>}
                </Button>
              </OverlayTrigger>
            )}
          </div>
        ) : <span className="text-muted small">N/A</span>;
      }

      // ── DOCUMENTOS DESPUÉS DEL CORTE → botones Certicámara ──
      const tieneArchivo = tieneRutaLocal || esCerticamaraFirmado;

      if (!tieneArchivo) {
        return (
          <OverlayTrigger overlay={<Tooltip>Esperando firma digital de Certicámara</Tooltip>}>
            <span>
              <Spinner size="sm" variant="primary" className="me-1" />
              <small className="text-muted">Procesando...</small>
            </span>
          </OverlayTrigger>
        );
      }

      return (
        <div className="d-flex justify-content-center gap-1 flex-wrap">
          {/* Ojito — documento firmado de Certicámara */}
          <OverlayTrigger overlay={<Tooltip>Ver documento firmado (Certicámara)</Tooltip>}>
            <Button variant="primary" size="sm" onClick={() => handleVerDocumentoCerticamara(doc)} disabled={!!estaDescargando}>
              {estaDescargando === 'ver' ? <Spinner size="sm" /> : <i className="bi bi-eye-fill"></i>}
            </Button>
          </OverlayTrigger>

          {/* Certificado Certicámara */}
          {esCerticamaraFirmado && (
            <OverlayTrigger overlay={<Tooltip>Certificado de firma digital (Certicámara)</Tooltip>}>
              <Button variant="outline-info" size="sm" onClick={() => handleDescargarCertificado(doc)} disabled={!!estaCertificando}>
                {estaCertificando ? <Spinner size="sm" /> : <i className="bi bi-award"></i>}
              </Button>
            </OverlayTrigger>
          )}

          {/* Certificado BIOFIRMAS */}
          {doc.rutaCertificadoBiofirmas && (
            <OverlayTrigger overlay={<Tooltip>Certificado BIOFIRMAS (selfie + firma + GPS)</Tooltip>}>
              <Button variant="success" size="sm" onClick={() => handleDescargarCertificadoBiofirmas(doc)}
                disabled={!!descargandoCerticamara[`biofirma-${doc._id}`]}>
                {descargandoCerticamara[`biofirma-${doc._id}`] ? <Spinner size="sm" /> : <i className="bi bi-download"></i>}
              </Button>
            </OverlayTrigger>
          )}
        </div>
      );
    }

    return null;
  };

  // ═══════════════════════════════════════════════════════════════════
  // PERMISOS Y DATOS
  // ═══════════════════════════════════════════════════════════════════
  useEffect(() => {
    const permisos = localStorage.getItem('permisos');
    if (permisos) {
      const p = JSON.parse(permisos);
      if (!p.verDocumentosMedicos) setSinPermisos(true);
    } else { setSinPermisos(true); }
  }, []);

  const obtenerDocumentosMedicos = async () => {
    try {
      const params = {
        page: pagina, limit: documentosPorPagina, tipo: 'medico',
        cedula: filtros.cedula || undefined, correo: filtros.correo || undefined,
        funcionario: filtros.funcionario || undefined, estado: filtros.estado || undefined,
        fechaDesde: filtros.fechaDesde || undefined, fechaHasta: filtros.fechaHasta || undefined
      };
      Object.keys(params).forEach(key => params[key] === undefined && delete params[key]);
      const datos = await apiGet('/api/documentos-dashboard', { params });
      setDocumentos(Array.isArray(datos.documentos) ? datos.documentos : []);
      setTotal(Number(datos.total || 0));
    } catch (err) { console.error('Error:', err); }
  };

  useEffect(() => {
    if (sinPermisos) return;
    obtenerDocumentosMedicos();
    const filtrosActivos = Object.values(filtros).some(v => v.trim() !== '');
    if (!filtrosActivos) {
      const id = setInterval(obtenerDocumentosMedicos, 15000);
      return () => clearInterval(id);
    }
  }, [pagina, sinPermisos, filtros]);

  const handleLimpiarFiltros = () => {
    setFiltros({ cedula: '', correo: '', funcionario: '', estado: '', fechaDesde: '', fechaHasta: '' });
    setPagina(1);
    setTimeout(() => obtenerDocumentosMedicos(), 100);
  };

  const hayFiltrosActivos = Object.values(filtros).some(v => v !== '');
  const handleCerrarModal = () => { setMostrarModal(false); setDocumentoSeleccionado(null); };

  // ⭐ FIX DESCARGA: descarga el PDF como blob sin recargar la página.
  // El atributo <a download> nativo NO funciona cuando el PDF viene de otra
  // ruta/origen del backend (el navegador ignora "download" y navega a la URL,
  // lo que parece una "recarga"). Aquí lo bajamos con fetch y forzamos la
  // descarga con una URL temporal del blob (mismo origen), luego liberamos memoria.
  const descargarPDF = async (url, nombreArchivo = 'documento.pdf') => {
    try {
      const respuesta = await fetch(url);
      if (!respuesta.ok) throw new Error('No se pudo obtener el archivo');
      const blob = await respuesta.blob();
      const urlBlob = URL.createObjectURL(blob);
      const enlace = document.createElement('a');
      enlace.href = urlBlob;
      enlace.download = nombreArchivo;
      document.body.appendChild(enlace);
      enlace.click();
      document.body.removeChild(enlace);
      URL.revokeObjectURL(urlBlob);
    } catch (error) {
      console.error('Error al descargar el PDF:', error);
      alert('No se pudo descargar el documento. Intente de nuevo.');
    }
  };

  const handleAbrirReenvio = (doc) => {
    setDocumentoReenvio(doc);
    setCorreoReenvio(doc.correo || '');
    setCelularReenvio(doc.celular || '');
    setMetodosReenvio(doc.sendMethods || ['correo']);
    setMensajeReenvio('');
    setMostrarModalReenvio(true);
  };

  const handleToggleMetodo = (metodo) => {
    setMetodosReenvio(prev => prev.includes(metodo) ? prev.filter(m => m !== metodo) : [...prev, metodo]);
  };

  const handleReenviarDocumento = async () => {
    if (!correoReenvio && !celularReenvio) { setMensajeReenvio('Debes ingresar al menos un correo o celular'); return; }
    if (correoReenvio && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(correoReenvio)) { setMensajeReenvio('El correo no es válido'); return; }
    if (celularReenvio && !/^\d{10}$/.test(celularReenvio)) { setMensajeReenvio('El celular debe tener 10 dígitos'); return; }
    if (metodosReenvio.length === 0) { setMensajeReenvio('Selecciona al menos un método'); return; }
    setEnviandoReenvio(true);
    setMensajeReenvio('Reenviando...');
    try {
      await apiPost(`/api/reenviar-documento/${documentoReenvio._id}`, { correo: correoReenvio, celular: celularReenvio, sendMethods: metodosReenvio });
      setMensajeReenvio('✅ Reenviado exitosamente');
      setTimeout(() => { setMostrarModalReenvio(false); obtenerDocumentosMedicos(); }, 2000);
    } catch (error) {
      setMensajeReenvio(`❌ Error: ${error.response?.data?.message || 'No se pudo reenviar'}`);
    } finally { setEnviandoReenvio(false); }
  };

  const renderPaginacion = () => {
    if (totalPaginas <= 1) return null;
    const items = [];
    items.push(<Pagination.Prev key="prev" disabled={pagina === 1} onClick={() => setPagina(p => p - 1)} />);
    const start = Math.max(1, pagina - 2);
    const end = Math.min(totalPaginas, start + 4);
    const adjustedStart = Math.max(1, end - 4);
    if (adjustedStart > 1) { items.push(<Pagination.Item key={1} onClick={() => setPagina(1)}>1</Pagination.Item>); if (adjustedStart > 2) items.push(<Pagination.Ellipsis key="e1" disabled />); }
    for (let i = adjustedStart; i <= end; i++) items.push(<Pagination.Item key={i} active={i === pagina} onClick={() => setPagina(i)}>{i}</Pagination.Item>);
    if (end < totalPaginas) { if (end < totalPaginas - 1) items.push(<Pagination.Ellipsis key="e2" disabled />); items.push(<Pagination.Item key={totalPaginas} onClick={() => setPagina(totalPaginas)}>{totalPaginas}</Pagination.Item>); }
    items.push(<Pagination.Next key="next" disabled={pagina === totalPaginas} onClick={() => setPagina(p => p + 1)} />);
    return <Pagination className="justify-content-center mb-0" size={isMobile ? 'sm' : 'md'}>{items}</Pagination>;
  };

  const getMainContentStyle = () => isMobile
    ? { marginLeft: 0, width: '100%', padding: '70px 15px 20px 15px', transition: 'all 0.3s ease' }
    : { marginLeft: sidebarAbierto ? '250px' : '70px', width: sidebarAbierto ? 'calc(100% - 250px)' : 'calc(100% - 70px)', padding: '20px 30px', transition: 'all 0.3s ease' };

  // ─── Card móvil ───
  const RowCard = ({ doc }) => (
    <Card className="mb-3 shadow-sm">
      <Card.Body>
        <div className="d-flex justify-content-between align-items-start mb-2">
          <div>
            <h6 className="mb-1">{decodificarNombre(doc.nombres)} <small className="text-muted ms-2">CC: {doc.cedula}</small></h6>
            <small className="text-muted d-block">{doc.correo || 'Sin correo'}</small>
          </div>
          {estadoBadge(doc.estado)}
        </div>
        <hr />
        <div className="mb-1 small"><strong>Documento:</strong> {doc.nombreDocumento}</div>
        <div className="mb-1 small"><strong>Enviado por:</strong> {decodificarNombre(doc.enviadoPor) || 'N/A'}</div>
        <div className="mb-1 small"><strong>Métodos:</strong> {renderMetodoEnvio(doc.sendMethods)}</div>
        <div className="mb-2 small"><strong>Fecha:</strong> {new Date(doc.createdAt).toLocaleDateString('es-CO')}</div>
        {usaCerticamara(doc) && (
          <div className="mb-2">
            <Badge bg="info" style={{ fontSize: '0.7rem' }}>
              <i className="bi bi-shield-check me-1"></i>Certificado Certicámara
            </Badge>
          </div>
        )}
        <div className="mt-2"><AccionesDocumento doc={doc} /></div>
      </Card.Body>
    </Card>
  );

  if (sinPermisos) return (
    <div className="d-flex" style={{ minHeight: '100vh' }}>
      <Sidebar onToggle={setSidebarAbierto} />
      <div className="flex-grow-1 d-flex align-items-center justify-content-center" style={getMainContentStyle()}>
        <Alert variant="danger" className="text-center"><h4>⛔ Acceso Denegado</h4><p className="mb-0">No tienes permisos para ver documentos médicos.</p></Alert>
      </div>
    </div>
  );

  return (
    <div className="d-flex" style={{ minHeight: '100vh', backgroundColor: '#f8f9fa' }}>
      <Sidebar onToggle={setSidebarAbierto} />

      <div className="flex-grow-1" style={getMainContentStyle()}>
        {/* Header */}
        <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center mb-3 mb-md-4">
          <div className="mb-2 mb-md-0">
            <h2 className="mb-1" style={{ fontSize: isMobile ? '1.3rem' : '1.75rem' }}>🏥 Documentos Médicos</h2>
            <p className="text-muted mb-0 small">Gestión y seguimiento de documentos médicos</p>
          </div>
          <div className="text-start text-md-end">
            <div className="fw-bold" style={{ fontSize: '1.1rem', color: '#1a1e4b' }}>👋 {decodificarNombre(nombres)}</div>
            <small className="text-muted text-capitalize">{(localStorage.getItem('rol') || 'usuario').replace('admin', ' Admin')}</small>
          </div>
        </div>

        {/* Filtros */}
        <Card className="shadow-sm mb-3 mb-md-4">
          <Card.Body className="p-3">
            <Form onSubmit={(e) => { e.preventDefault(); setPagina(1); obtenerDocumentosMedicos(); }}>
              <div className="row align-items-end g-2 mb-2">
                <div className="col-12 col-md-4">
                  <Form.Label className="mb-1 small"><i className="bi bi-person-vcard me-1"></i>Cédula</Form.Label>
                  <Form.Control type="text" placeholder="Buscar por cédula" value={filtros.cedula} onChange={(e) => setFiltros({ ...filtros, cedula: e.target.value })} size="sm" />
                </div>
                <div className="col-12 col-md-4">
                  <Form.Label className="mb-1 small"><i className="bi bi-envelope me-1"></i>Correo</Form.Label>
                  <Form.Control type="text" placeholder="Buscar por correo" value={filtros.correo} onChange={(e) => setFiltros({ ...filtros, correo: e.target.value })} size="sm" />
                </div>
                <div className="col-12 col-md-4">
                  <div className="d-flex gap-2">
                    <Button type="submit" variant="primary" className="flex-grow-1" size="sm"><i className="bi bi-search me-1"></i>Buscar</Button>
                    <Button variant="outline-secondary" size="sm" onClick={() => setMostrarFiltrosAvanzados(!mostrarFiltrosAvanzados)}>
                      <i className={`bi bi-funnel${mostrarFiltrosAvanzados ? '-fill' : ''}`}></i>
                    </Button>
                    {hayFiltrosActivos && <Button variant="outline-danger" onClick={handleLimpiarFiltros} size="sm"><i className="bi bi-x-circle"></i></Button>}
                  </div>
                </div>
              </div>

              {mostrarFiltrosAvanzados && (
                <div className="row align-items-end g-2 mt-2 pt-2 border-top">
                  <div className="col-12 col-md-3">
                    <Form.Label className="mb-1 small">Funcionario</Form.Label>
                    <Form.Control type="text" placeholder="Nombre del funcionario" value={filtros.funcionario} onChange={(e) => setFiltros({ ...filtros, funcionario: e.target.value })} size="sm" />
                  </div>
                  <div className="col-6 col-md-2">
                    <Form.Label className="mb-1 small">Estado</Form.Label>
                    <Form.Select value={filtros.estado} onChange={(e) => setFiltros({ ...filtros, estado: e.target.value })} size="sm">
                      <option value="">Todos</option>
                      <option value="pendiente">⌛ Pendiente</option>
                      <option value="en_firma">✍️ En firma</option>
                      <option value="firmado">✍️ Firmado</option>
                      <option value="finalizado">✅ Finalizado</option>
                      <option value="rechazado">❌ Rechazado</option>
                      <option value="anulado">🚫 Anulado</option>
                    </Form.Select>
                  </div>
                  <div className="col-6 col-md-2">
                    <Form.Label className="mb-1 small">Desde</Form.Label>
                    <Form.Control type="date" value={filtros.fechaDesde} onChange={(e) => setFiltros({ ...filtros, fechaDesde: e.target.value })} size="sm" />
                  </div>
                  <div className="col-6 col-md-2">
                    <Form.Label className="mb-1 small">Hasta</Form.Label>
                    <Form.Control type="date" value={filtros.fechaHasta} onChange={(e) => setFiltros({ ...filtros, fechaHasta: e.target.value })} size="sm" />
                  </div>
                  <div className="col-6 col-md-3">
                    <Button type="submit" variant="success" className="w-100" size="sm"><i className="bi bi-funnel me-1"></i>Aplicar Filtros</Button>
                  </div>
                </div>
              )}
            </Form>
            <div className="mt-2 text-muted small">
              <i className="bi bi-info-circle me-1"></i>
              Mostrando {documentos.length} de {total} documentos
            </div>
          </Card.Body>
        </Card>

        {/* Tabla Desktop */}
        <div className="d-none d-lg-block">
          <div className="table-responsive shadow-sm" style={{ maxHeight: '65vh', overflow: 'auto', borderRadius: '10px' }}>
            <Table bordered hover size="sm" className="mb-0">
              <thead className="table-primary" style={{ position: 'sticky', top: 0, zIndex: 10, backgroundColor: '#cfe2ff' }}>
                <tr>
                  <th style={{ width: '95px' }}>Fecha</th>
                  <th style={{ width: '100px' }}>Cédula</th>
                  <th style={{ minWidth: '180px' }}>Nombres</th>
                  <th style={{ minWidth: '190px' }}>Correo</th>
                  <th style={{ minWidth: '200px' }}>Documento</th>
                  <th style={{ width: '100px' }}>Estado</th>
                  <th style={{ minWidth: '130px' }}>Enviado por</th>
                  <th className="text-center" style={{ width: '75px' }}>Métodos</th>
                  <th className="text-center" style={{ width: '130px' }}>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {documentos.length === 0 ? (
                  <tr>
                    <td colSpan="9" className="text-center py-5">
                      <i className="bi bi-clipboard-x" style={{ fontSize: '3rem', color: '#6c757d' }}></i>
                      <p className="text-muted mt-3 mb-1">No hay documentos médicos</p>
                      {hayFiltrosActivos && <Button variant="outline-secondary" size="sm" onClick={handleLimpiarFiltros}>Limpiar filtros</Button>}
                    </td>
                  </tr>
                ) : (
                  documentos.map((doc) => (
                    <tr key={doc._id} className={doc.estado === 'rechazado' ? 'table-danger' : ''}>
                      <td className="small" style={{ whiteSpace: 'nowrap' }}>
                        {new Date(doc.createdAt).toLocaleDateString('es-CO')}<br />
                        <small className="text-muted">{new Date(doc.createdAt).toLocaleTimeString('es-CO', { hour: '2-digit', minute: '2-digit' })}</small>
                      </td>
                      <td style={{ whiteSpace: 'nowrap' }}>
                        <span className="fw-bold">{doc.cedula}</span>
                        <Button variant="link" size="sm" className="p-0 ms-1 align-baseline" onClick={() => copyToClipboard(doc.cedula)}>
                          <i className="bi bi-clipboard"></i>
                        </Button>
                      </td>
                      <td className="small" style={{ wordBreak: 'break-word' }}>{decodificarNombre(doc.nombres) || '-'}</td>
                      <td className="small" style={{ wordBreak: 'break-all', fontSize: '0.85rem' }}>{doc.correo || '-'}</td>
                      <td className="small" style={{ maxWidth: '250px', wordBreak: 'break-word' }}>
                        <OverlayTrigger placement="top" overlay={<Tooltip>{doc.nombreDocumento}</Tooltip>}>
                          <div style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', cursor: 'help' }}>
                            {doc.nombreDocumento || '-'}
                          </div>
                        </OverlayTrigger>
                        {usaCerticamara(doc) && (
                          <Badge bg="info" style={{ fontSize: '0.65rem' }}>🔒 Certicámara</Badge>
                        )}
                      </td>
                      <td>{estadoBadge(doc.estado)}</td>
                      <td className="small" style={{ wordBreak: 'break-word', fontSize: '0.85rem' }}>
                        {doc.enviadoPor && doc.enviadoPor !== 'No identificado' ? decodificarNombre(doc.enviadoPor) : <span className="text-muted fst-italic">Sin identificar</span>}
                      </td>
                      <td className="text-center">{renderMetodoEnvio(doc.sendMethods)}</td>
                      <td className="text-center" style={{ verticalAlign: 'middle' }}>
                        <AccionesDocumento doc={doc} />
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          </div>
        </div>

        {/* Cards móvil */}
        <div className="d-block d-lg-none">
          {documentos.length === 0 ? (
            <div className="text-center py-5">
              <i className="bi bi-clipboard-x" style={{ fontSize: '3rem', color: '#6c757d' }} />
              <p className="text-muted mt-3">No hay documentos médicos</p>
              {hayFiltrosActivos && <Button variant="outline-secondary" size="sm" onClick={handleLimpiarFiltros}>Limpiar filtros</Button>}
            </div>
          ) : documentos.map((doc) => <RowCard key={doc._id} doc={doc} />)}
        </div>

        <div className="mt-3">{renderPaginacion()}</div>

        {/* Modal ver documento */}
        <Modal show={mostrarModal} onHide={handleCerrarModal} centered size="xl" fullscreen="lg-down">
          <Modal.Header closeButton><Modal.Title>📄 Documento</Modal.Title></Modal.Header>
          <Modal.Body className="p-0" style={{ height: '80vh' }}>
            {documentoSeleccionado && <iframe src={documentoSeleccionado} title="PDF" width="100%" height="100%" style={{ border: 'none' }} />}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleCerrarModal}>Cerrar</Button>
{documentoSeleccionado && <Button variant="success" onClick={() => descargarPDF(documentoSeleccionado, 'documento.pdf')}><i className="bi bi-download me-1"></i>Descargar</Button>}          </Modal.Footer>
        </Modal>

        {/* Modal reenvío */}
        <Modal show={mostrarModalReenvio} onHide={() => setMostrarModalReenvio(false)} centered>
          <Modal.Header closeButton><Modal.Title><i className="bi bi-arrow-repeat me-2"></i>Reenviar Documento</Modal.Title></Modal.Header>
          <Modal.Body>
            {documentoReenvio && (
              <div>
                <div className="mb-3 p-3 bg-light rounded">
                  <div className="small text-muted">Documento:</div>
                  <div className="fw-bold">{documentoReenvio.nombreDocumento}</div>
                  <div className="small text-muted mt-1">Paciente: {decodificarNombre(documentoReenvio.nombres)} — CC: {documentoReenvio.cedula}</div>
                </div>
                <Form>
                  <Form.Group className="mb-3">
                    <Form.Label><i className="bi bi-envelope me-2"></i>Correo</Form.Label>
                    <Form.Control type="email" placeholder="correo@ejemplo.com" value={correoReenvio} onChange={(e) => setCorreoReenvio(e.target.value)} disabled={enviandoReenvio} />
                  </Form.Group>
                  <Form.Group className="mb-3">
                    <Form.Label><i className="bi bi-phone me-2"></i>Celular</Form.Label>
                    <Form.Control type="tel" placeholder="3001234567" value={celularReenvio} onChange={(e) => setCelularReenvio(e.target.value.replace(/\D/g, '').slice(0, 10))} disabled={enviandoReenvio} />
                  </Form.Group>
                  <Form.Group className="mb-3">
                    <Form.Label className="d-block mb-2"><i className="bi bi-send me-2"></i>Métodos de Envío</Form.Label>
                    <div className="d-flex flex-wrap gap-3">
                      <Form.Check type="checkbox" id="m-correo" label={<><i className="bi bi-envelope-fill me-1"></i>Correo</>} checked={metodosReenvio.includes('correo')} onChange={() => handleToggleMetodo('correo')} disabled={enviandoReenvio || !correoReenvio} />
                      <Form.Check type="checkbox" id="m-sms" label={<><i className="bi bi-chat-dots-fill me-1"></i>SMS</>} checked={metodosReenvio.includes('sms')} onChange={() => handleToggleMetodo('sms')} disabled={enviandoReenvio || !celularReenvio} />
                      <Form.Check type="checkbox" id="m-whatsapp" label={<><i className="bi bi-whatsapp me-1" style={{ color: '#25D366' }}></i>WhatsApp</>} checked={metodosReenvio.includes('whatsapp')} onChange={() => handleToggleMetodo('whatsapp')} disabled={enviandoReenvio || !celularReenvio} />
                    </div>
                  </Form.Group>
                </Form>
                {mensajeReenvio && <Alert variant={mensajeReenvio.includes('✅') ? 'success' : mensajeReenvio.includes('❌') ? 'danger' : 'info'}>{mensajeReenvio}</Alert>}
              </div>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setMostrarModalReenvio(false)} disabled={enviandoReenvio}>Cancelar</Button>
            <Button variant="primary" onClick={handleReenviarDocumento} disabled={enviandoReenvio || (!correoReenvio && !celularReenvio)}>
              {enviandoReenvio ? <><Spinner size="sm" className="me-2" />Reenviando...</> : <><i className="bi bi-arrow-repeat me-2"></i>Reenviar</>}
            </Button>
          </Modal.Footer>
        </Modal>
      </div>
    </div>
  );
}

export default MedicalDocumentsPage;